import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import api from '../services/api';
import websocketService from '../services/websocket';
import { User, LoginCredentials, RegisterData, AuthResponse } from '../types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  register: (data: RegisterData, inviteToken?: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Prevent double initialization (React StrictMode)
  const initRef = useRef(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    // Guard against double execution
    if (initRef.current) return;
    initRef.current = true;
    
    const initAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        setToken(storedToken);
        try {
          // Fetch current user info
          const response = await api.get<{ data: User }>('/auth/me');
          setUser(response.data.data || null);
          
          // Note: WebSocket connection is established when joining a specific session
        } catch (error) {
          console.error('Failed to fetch user info:', error);
          // Clear invalid token
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const loginWithToken = async (newToken: string): Promise<void> => {
    try {
      // Store token
      localStorage.setItem('authToken', newToken);
      setToken(newToken);
      
      // Fetch user info
      const response = await api.get<{ data: User }>('/auth/me');
      setUser(response.data.data || null);
    } catch (error) {
      console.error('Login with token failed:', error);
      localStorage.removeItem('authToken');
      setToken(null);
      throw error;
    }
  };

  const login = async (credentials: LoginCredentials): Promise<void> => {
    try {
      const response = await api.post<{data: AuthResponse}>('/auth/login', credentials);
      const { token: newToken, user: newUser } = response.data.data;
      
      // Store token
      localStorage.setItem('authToken', newToken);
      setToken(newToken);
      setUser(newUser);
      
      // Note: WebSocket connection is established when joining a specific session
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const register = async (data: RegisterData, inviteToken?: string): Promise<void> => {
    try {
      const url = inviteToken ? `/invites/${inviteToken}/register` : '/auth/register';
      const response = await api.post<{data: AuthResponse | any}>(url, data);
      
      // The invite endpoint returns { token, user, sessionId, teamId }
      // The normal endpoint returns { token, user }
      // Both have token and user at the top level of data or inside data.data depending on wrapper
      // Our api client usually handles unwrapping if we typed it, but here we access manually.
      // Based on previous code: const { token: newToken, user: newUser } = response.data.data;
      
      const { token: newToken, user: newUser } = response.data.data;
      
      // Store token
      localStorage.setItem('authToken', newToken);
      setToken(newToken);
      setUser(newUser);
      
      // Note: WebSocket connection is established when joining a specific session
    } catch (error) {
      console.error('Registration failed:', error);
      throw error;
    }
  };

  const logout = (): void => {
    // Clear token
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    
    // Disconnect WebSocket
    websocketService.disconnect();
  };

  const refreshUser = async (): Promise<void> => {
    if (!token) return;
    
    try {
      const response = await api.get<{ data: User }>('/auth/me');
      setUser(response.data.data || null);
    } catch (error) {
      console.error('Failed to refresh user info:', error);
      // If refresh fails, clear auth state
      localStorage.removeItem('authToken');
      setToken(null);
      setUser(null);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    login,
    loginWithToken,
    register,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
